import { useLightMode } from '@/hooks/useLightMode'
import { useTranslation } from 'react-i18next'

import {
  DarkProvider,
  SegmentedControl,
  SegmentedControlOption,
  SegmentedControlOptionState,
} from '@hivewatch/dark'

import { HivewatchIcon } from './HivewatchIcon'

export type ViewToolbarView = 'live' | 'focused' | 'extended'
export type ViewToolbarTabState = SegmentedControlOptionState

export interface ViewToolbarIProps {
  activeView: ViewToolbarView
  focusedState: ViewToolbarTabState
  extendedState: ViewToolbarTabState
  focusedHasDot?: boolean
  extendedHasDot?: boolean
  focusedLabel?: string
  extendedLabel?: string
  onSelect: (view: ViewToolbarView) => void
}

export const ViewToolbar = ({
  activeView,
  focusedState,
  extendedState,
  focusedHasDot = false,
  extendedHasDot = false,
  focusedLabel = 'Focused Clip',
  extendedLabel = 'Extended Clip',
  onSelect,
}: ViewToolbarIProps) => {
  const mode = useLightMode()
  const { t } = useTranslation()

  const hasFocusedFailed = focusedState === 'error'

  const options: SegmentedControlOption[] = [
    { value: 'live', label: 'Live' },
    {
      value: 'focused',
      label: focusedLabel,
      icon: <HivewatchIcon />,
      state: focusedState,
      hasDot: focusedHasDot,
      tooltip: hasFocusedFailed
        ? t('incidents:view_toolbar_focused_clip_error_tooltip')
        : undefined,
    },
    // If the focused clip failed, the extended clip is not coming either — drop the view rather
    // than advertising one that can never load.
    ...(hasFocusedFailed
      ? []
      : [
          {
            value: 'extended',
            label: extendedLabel,
            state: extendedState,
            hasDot: extendedHasDot,
            tooltip:
              extendedState === 'error'
                ? t('incidents:view_toolbar_extended_clip_error_tooltip')
                : undefined,
          },
        ]),
  ]

  return (
    <DarkProvider>
      <SegmentedControl
        colorScheme={mode === 'light' ? 'light' : 'dark'}
        height='32px'
        onChange={(v) => onSelect(v as ViewToolbarView)}
        options={options}
        value={activeView}
        width='fit-content'
      />
    </DarkProvider>
  )
}
